import { useEffect, useState } from 'react';
import {
  AirKey,
  // @ts-expect-error not needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  AirKeyIntroBottomSheet,
  // @ts-expect-error not needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  CardTapBottomSheet,
} from 'airkey-mfa-react';
import { compareVersions } from 'compare-versions';
import ky from 'ky';
import { LoadScreen } from '@/components/LoadScreen';
import { useAirKeyTrace, useAirKeyTracePageView } from '@/utils/airKeyTrace';
import {
  isPaymentHandlerWindow,
  reportTerminalOutcome,
} from '@/utils/paymentHandlerBridge';
import warningIcon from '@assets/Caution.svg';
import failureIcon from '@assets/failure.svg';
import fraudsterIcon from '@assets/Fraudster.svg';
import successIcon from '@assets/success.svg';
import { Button } from '@cof/omni-react';
import { colorBackgroundDisabled } from '@cof/omni-tokens';
import { NRExperimentInfo, TapCardPage } from '@components/3ds';
import { NotSupported } from '@components/3ds/NotSupported';
import { ErrorScreen_DEPRECIATED } from '@components/ErrorScreen';
import { useUAParser } from '@hooks/useUAParser';
import { TransactionDetailsScreen } from './TransactionDetailsScreen';
// import { serverName } from '../util';

const client = ky.create({
  headers: {
    Accept: 'application/json;v=1',
    'Airkey-Version': '1',
    'Content-Type': 'application/json',
    'User-Agent': navigator.userAgent,
  },
  retry: {
    limit: 2,
    methods: ['get', 'post'],
    backoffLimit: 3000, // 3 seconds
  },
  timeout: 60000, // 60 seconds
});

interface FraudSuspectedData {
  reason: string;
}
const fraudData: FraudSuspectedData = {
  reason: 'FraudSuspected',
};

const IS_DEV = !!import.meta.env.DEV;
const IS_TEST = !!import.meta.env.TEST;

const ENABLED_BROWSERS: { [key: string]: string } = {
  Chrome: '89',
};

const isValidVersion = (name: string, version: string): boolean => {
  const minVersion = ENABLED_BROWSERS[name];
  return (
    minVersion !== undefined && compareVersions(version, minVersion) !== -1
  );
};

export function AndroidScreen({
  processId,
  isDemo,
}: {
  processId: string;
  isDemo?: boolean;
}) {
  const { browser } = useUAParser();
  const [isEnabled, setEnabled] = useState(isDemo);
  const [isLoading, setLoading] = useState(true);

  useAirKeyTracePageView('card_tap');

  useEffect(() => {
    if (browser.name && browser.version) {
      // alert(JSON.stringify(browser));
      if (IS_DEV || IS_TEST) {
        // Simulate NFC in DEV
        AirKey.setSimulateNfc(true);
        setEnabled(true);
      } else {
        setEnabled(
          'NDEFReader' in window &&
            isValidVersion(browser.name, browser.version),
        );
      }
      setLoading(false);
    }
  }, [browser]);

  if (isLoading) return <LoadScreen />;
  if (isEnabled)
    return <TransactionDetails processId={processId} isDemo={isDemo} />;
  else return <NotSupported />;
}

export function TransactionDetails({
  processId,
  isDemo,
}: {
  processId: string;
  isDemo?: boolean;
}) {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [details, setDetails] = useState<any>(null);
  const [showAirkey, setShowAirkey] = useState(isDemo);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isUnrecognized, setIsUnrecognized] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [isFailure, setFailure] = useState(false);
  const [isRetry, setRetry] = useState(false);
  const [retryClicked, setRetryClicked] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [nrExperimentData, setNrExperimentData] =
    useState<NRExperimentInfo | null>(null);

  useAirKeyTracePageView('transaction_details');

  // In the payment-handler window (?c=1), report the terminal outcome to the
  // service worker; no-op otherwise. "Unrecognized" is a user decline, so it
  // maps to a cancellation.
  useEffect(() => {
    if (!isPaymentHandlerWindow()) return;
    reportTerminalOutcome({
      success: isSuccess,
      cancelled: isCanceled || isUnrecognized,
      verificationFailed: isFailure,
      errorType: isInvalid ? 'invalid' : null,
      policyProcessId: processId,
    });
  }, [isSuccess, isCanceled, isUnrecognized, isFailure, isInvalid, processId]);

  // 1. Fetch transaction details
  async function getTransaction(pId: string) {
    try {
      const responseDetails = await client
        .get(`/protected/1066746/3dsecure/${pId}/retrieve-public-context`)
        .json();
      if (responseDetails) {
        setDetails(responseDetails);
      } else {
        setIsInvalid(true);
      }
    } catch (error: any) {
      setIsInvalid(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (processId) {
      if (isDemo)
        setDetails({
          purchaseAmount: '€520.89 EUR',
          cleansedMerchantName: 'Starbucks',
          cardLastFour: '1234',
          merchantLogo: 'https://ecm.capitalone.com/MDX/logo/netflix.png',
        });
      else getTransaction(processId);
    }
  }, [isDemo, processId]);

  if (isInvalid)
    return (
      <InvalidTransaction
        experimentKey={nrExperimentData?.experimentKey}
        policyProcessId={nrExperimentData?.policyProcessId}
        variant={nrExperimentData?.variant}
      />
    );

  if (isUnrecognized) {
    return (
      <TransactionUnrecognized
        experimentKey={nrExperimentData?.experimentKey}
        policyProcessId={nrExperimentData?.policyProcessId}
        variant={nrExperimentData?.variant}
      />
    );
  }

  if (isCanceled) {
    return <TransactionCancelled />;
  }

  if (showAirkey) {
    return (
      <TapCardPage
        processId={processId}
        onClose={() => {
          setIsCanceled(true);
        }}
        onRead={async (airKeyMessage) => {
          // Hide transaction details to prevent flashing
          setDetails(null);
          setShowAirkey(false);
          try {
            const data = await client
              .post(
                `/protected/1066746/3dsecure/${processId}/validate-message`,
                { json: { airKeyMessage } },
              )
              .json();
            // Handle validation result
            if (
              (data as { validationResult: string }).validationResult ===
              'SUCCESS'
            ) {
              setSuccess(true);
            } else if (
              (data as { validationResult: string }).validationResult ===
              'FAILURE'
            ) {
              setFailure(true);
            } else if (
              (data as { validationResult: string }).validationResult ===
              'RETRY'
            ) {
              setRetry(true);
            }
          } catch (error) {
            setFailure(true);
          }
        }}
      />
    );
  }

  if (isRetry) {
    if (retryClicked) {
      return (
        <TapCardPage
          processId={processId}
          onClose={() => {
            setIsCanceled(true);
          }}
          onRead={async (airKeyMessage) => {
            setShowAirkey(false);

            try {
              const data = await client
                .post(
                  `/protected/1066746/3dsecure/${processId}/validate-message`,
                  { json: { airKeyMessage } },
                )
                .json();
              // Handle validation result
              if (
                (data as { validationResult: string }).validationResult ===
                'SUCCESS'
              ) {
                setSuccess(true);
              } else {
                setFailure(true);
              }
              setRetry(false);
            } catch (error) {
              setFailure(true);
              setRetry(false);
            }
          }}
        />
      );
    } else {
      return (
        <Resync
          handleClick={() => {
            setRetryClicked(true);
          }}
          experimentKey={nrExperimentData?.experimentKey}
          policyProcessId={nrExperimentData?.policyProcessId}
          variant={nrExperimentData?.variant}
        />
      );
    }
  }

  if (isSuccess) {
    return (
      <VerificationComplete
        experimentKey={nrExperimentData?.experimentKey}
        policyProcessId={nrExperimentData?.policyProcessId}
        variant={nrExperimentData?.variant}
      />
    );
  }

  if (isFailure) {
    return (
      <VerificationFailed
        experimentKey={nrExperimentData?.experimentKey}
        policyProcessId={nrExperimentData?.policyProcessId}
        variant={nrExperimentData?.variant}
      />
    );
  }

  if (details) {
    return (
      <TransactionDetailsScreen
        processId={processId}
        cardLastFour={details.cardLastFour as string}
        merchantLogo={details.merchantLogo as string}
        merchantName={details.merchantName as string}
        onApprove={(variantName, experimentKey, policyProcessId) => {
          setNrExperimentData({
            variant: variantName,
            experimentKey: experimentKey,
            policyProcessId: policyProcessId,
          });
          setShowAirkey(true);
        }}
        onDecline={(variantName, experimentKey, policyProcessId) => {
          setNrExperimentData({
            variant: variantName,
            experimentKey: experimentKey,
            policyProcessId: policyProcessId,
          });
          setIsUnrecognized(true);
          client.post(
            `/protected/1066746/3dsecure/${processId}/invalidate-process`,
            { json: fraudData },
          );
        }}
        purchaseAmount={details.purchaseAmount as string}
      />
    );
  }

  if (isLoading) {
    return <LoadScreen />;
  }
}

export function Currency({ value }: { value: string }) {
  const [amount, currencyCode] = value.split(' ') || [];
  const currencySymbol = amount?.[0] || '';
  const [integerPart, decimalPart] = amount.substr(1).split('.');
  return (
    <div className="amount">
      <span className="currency">
        {currencySymbol}
        <span className="h3">{integerPart}</span>
        <small>{decimalPart}</small>
      </span>
      <strong className="currency-code">{currencyCode}</strong>
    </div>
  );
}

export function MerchantInfo({ name, logo }: { name: string; logo: string }) {
  const [imageSrc, setImageSrc] = useState(logo);
  return (
    <div className="merchant">
      <div>
        <div className="merchant-name">{name}</div>
      </div>
      {imageSrc && (
        <img
          src={imageSrc}
          alt={name}
          onError={() => setImageSrc('')}
          height={32}
        />
      )}
    </div>
  );
}

export function Resync({
  handleClick,
  experimentKey,
  policyProcessId,
  variant,
}: {
  handleClick?: () => void;
  experimentKey: string | undefined;
  policyProcessId: string | undefined;
  variant: string | undefined;
}) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'resync',
      action: 'view_loaded',
      additionalData: {
        message: 'Android resync view has been triggered',
        experiment_key: experimentKey,
        policy_process_id: policyProcessId,
        variant,
      },
    });
  }, [experimentKey, policyProcessId, variant]);

  return (
    <div className="not-supported-dialog" data-testid="Android-Resync">
      <div
        style={{
          padding: '40px',
          borderRadius: '100%',
          backgroundColor: colorBackgroundDisabled,
        }}
      >
        <img src={warningIcon} width="66" alt="" role="presentation" />
      </div>
      <h3>Let&apos;s Do That One More Time</h3>
      <div className="text-center">
        We&apos;re almost there! We just need to read your card one more time
        for security.
      </div>
      <div
        style={{
          width: '100%',
          justifyContent: 'center',
          position: 'absolute',
          bottom: '50px',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Button
          style={{
            width: '100%',
            maxWidth: '400px',
          }}
          rank="primary"
          size="medium"
          onClick={handleClick}
          data-testid="Android-Resync--Button"
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}

export function TransactionUnrecognized({
  experimentKey,
  policyProcessId,
  variant,
}: NRExperimentInfo) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'transaction_unrecognized',
      action: 'view_loaded',
      additionalData: {
        message: 'Transaction Unrecognized screen has been triggered',
        experiment_key: experimentKey,
        policy_process_id: policyProcessId,
        variant,
      },
    });
  }, [experimentKey, policyProcessId, variant]);

  return (
    <div className="transaction" data-testid="Android-TransactionUnrecognized">
      <header>
        <h1 className="header">Transaction Cancelled</h1>
      </header>
      <div className="cancelled-dialog">
        <img
          src={fraudsterIcon}
          width="171"
          alt=""
          role="presentation"
          className="img-border"
        />
        <div className="h4">This transaction has been canceled</div>
        <div className="text-center">
          Thank you for letting us know you don&apos;t recognize this purchase.
        </div>
      </div>
    </div>
  );
}

export function TransactionCancelled() {
  useAirKeyTracePageView('transaction_cancelled');

  return (
    <ErrorScreen_DEPRECIATED
      title="Transaction Canceled"
      body="Your transaction could not be verified. Please return to merchant site and try again."
      header="Transaction Canceled"
    />
  );
}

export function InvalidTransaction({
  experimentKey,
  policyProcessId,
  variant,
}: NRExperimentInfo) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'invalid_transaction',
      action: 'view_loaded',
      additionalData: {
        message: 'Invalid Transaction screen has been triggered',
        experiment_key: experimentKey,
        policy_process_id: policyProcessId,
        variant,
      },
    });
  }, [experimentKey, policyProcessId, variant]);

  return (
    <div
      className="not-supported-dialog"
      data-testid="Android-InvalidTransaction"
    >
      <img src={warningIcon} width="66" alt="" role="presentation" />
      <h3>This transaction is invalid</h3>
      <div className="text-center">
        Please return to the merchant site and try again.
      </div>
    </div>
  );
}

export function VerificationComplete({
  experimentKey,
  policyProcessId,
  variant,
}: NRExperimentInfo) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'outro',
      action: 'view_loaded',
      additionalData: {
        message: 'Verification Complete screen has been triggered',
        experiment_key: experimentKey,
        policy_process_id: policyProcessId,
        variant,
      },
    });
  }, [experimentKey, policyProcessId, variant]);

  return (
    <div className="transaction" data-testid="Android-VerificationComplete">
      <header>
        <h1 className="header">Verification complete</h1>
      </header>
      <div className="success-screen">
        <div className="elipse">
          <img
            src={successIcon}
            width="171"
            alt=""
            role="presentation"
            className="img-border"
          />
        </div>
        <div className="h4">Verification complete</div>
        <p>
          Your transaction has been verified. You can now return to the merchant
          site to confirm your purchase.
        </p>
      </div>
    </div>
  );
}

export function VerificationFailed({
  experimentKey,
  policyProcessId,
  variant,
}: NRExperimentInfo) {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'verification_failed',
      action: 'view_loaded',
      additionalData: {
        message: 'Verification Failed screen has been triggered',
        experiment_key: experimentKey,
        policy_process_id: policyProcessId,
        variant,
      },
    });
  }, [experimentKey, policyProcessId, variant]);

  return (
    <div className="transaction" data-testid="Android-VerificationFailed">
      <header>
        <h1 className="header">Verification failed</h1>
      </header>
      <div className="failure-screen">
        {/* <div className='elipse'>  */}
        <img
          src={failureIcon}
          width="171"
          alt=""
          role="presentation"
          className="img-border"
        />
        {/* </div> */}
        <div className="h4">Verification failed</div>
        <p>
          Your transaction could not be verified. Please return to merchant site
          and try again.
        </p>
      </div>
    </div>
  );
}
